// Mobile Navigation Component for PlexiSystem 4.0
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronRight, Home } from 'lucide-react';
import type { MenuCategory } from '../../config/menuConfig';
import { AnimatedBackground } from './AnimatedBackground';

interface MobileNavigationProps {
  categories: MenuCategory[];
  onClose: () => void;
}

export const MobileNavigation: React.FC<MobileNavigationProps> = ({ 
  categories, 
  onClose 
}) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (href: string) => {
    navigate(href);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-2xl z-50 overflow-y-auto pb-safe">
      <AnimatedBackground />
      
      {/* Header */}
      <div className="sticky top-0 bg-black/50 backdrop-blur-xl border-b border-white/10 px-4 py-4 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          {activeCategory && (
            <button
              onClick={() => setActiveCategory(null)}
              className="p-2 hover:bg-white/10 rounded-xl transition-all"
              aria-label="Powrót"
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
          )}
          <h2 className="text-xl font-bold bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            {activeCategory 
              ? categories.find(c => c.id === activeCategory)?.label 
              : 'Menu PlexiSystem'}
          </h2>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-xl transition-all"
          aria-label="Zamknij menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Quick Actions Bar */}
      {!activeCategory && (
        <div className="px-4 py-3 border-b border-white/10 bg-white/5">
          <div className="flex gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => handleNavigate('/dashboard')}
              className="flex-shrink-0 px-4 py-2 bg-gradient-to-r from-violet-500/20 to-purple-500/20 rounded-xl text-sm font-medium whitespace-nowrap"
            >
              🏠 Dashboard
            </button>
            <button
              onClick={() => handleNavigate('/calculator')}
              className="flex-shrink-0 px-4 py-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl text-sm font-medium whitespace-nowrap"
            >
              🧮 Kalkulator
            </button>
            <button
              onClick={() => handleNavigate('/offers')}
              className="flex-shrink-0 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-xl text-sm font-medium whitespace-nowrap"
            >
              📄 Oferty
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {!activeCategory ? (
        <div className="p-4 space-y-3">
          {categories.map(category => {
            const Icon = category.icon;
            const visibleItems = category.items.filter(item => 
              !item.checkAccess || item.checkAccess()
            );
            
            if (visibleItems.length === 0) return null;
            
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className="w-full p-5 bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-2xl flex items-center justify-between hover:from-white/10 hover:to-white/15 transition-all transform hover:scale-[1.02] active:scale-[0.98] border border-white/10 group"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${category.gradient} shadow-lg group-hover:scale-110 transition-transform`}>
                    {category.emoji ? (
                      <span className="text-2xl">{category.emoji}</span>
                    ) : (
                      <Icon className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-white text-lg">{category.label}</p>
                    {category.description && (
                      <p className="text-xs text-gray-400 mt-1">{category.description}</p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">{visibleItems.length} modułów</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </button>
            );
          })}
        </div>
      ) : (
        <div className="p-4">
          <div className="space-y-2">
            {categories
              .find(c => c.id === activeCategory)
              ?.items.filter(item => !item.checkAccess || item.checkAccess())
              .sort((a, b) => (a.priority || 999) - (b.priority || 999))
              .map(item => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigate(item.href)}
                    className={`w-full p-4 rounded-xl flex items-center gap-3 transition-all transform active:scale-[0.98] ${
                      isActive 
                        ? 'bg-gradient-to-r from-violet-500/20 to-purple-500/20 border border-violet-500/50' 
                        : 'bg-white/5 hover:bg-white/10 border border-white/10'
                    }`}
                  >
                    <div className={`p-2.5 rounded-lg ${
                      isActive ? 'bg-violet-500/20' : 'bg-white/10'
                    } ${isActive ? 'scale-110' : ''} transition-all`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <span className="text-white font-medium">{item.label}</span>
                      {item.description && (
                        <p className="text-xs text-gray-400 mt-1">{item.description}</p>
                      )}
                    </div>
                    {item.badge && (
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        item.badge.gradient || 'bg-gradient-to-r from-violet-500 to-purple-500'
                      } ${item.badge.pulse ? 'animate-pulse' : ''} text-white font-medium`}>
                        {item.badge.text}
                      </span>
                    )}
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* Footer with version info */}
      <div className="mt-auto p-4 border-t border-white/10">
        <div className="text-center text-xs text-gray-500">
          <p>PlexiSystem v4.0.0</p>
          <p className="mt-1">© 2025 Crystal Clear Design</p>
        </div>
      </div>
    </div>
  );
};
